import { Flex, Text } from "@mantine/core";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import type { RouterContextType } from "@/api/permissions/permissions.api";
import type { StepFormStepId } from "@/api/sp-invite/spInvite.endpoints";
import type { IAlignmentApplicantInfoFormProps } from "@/components/data/alignmentApplicantInfoForm";
import JsonFormBuilder from "@/components/features/JsonFormBuilder/JsonFormBuilder";
import W2Coverage from "@/components/features/ServiceProviders/W2Coverage/W2Coverage";
import SummaryComponent from "@/components/features/SummaryComponent/SummaryComponent";
import Loader from "@/components/ui/Loader/Loader";
import { useSpInviteForm, useSpInviteJsonForm } from "@/hooks/sp-invite/useSpInviteQueries";
import { useSpInviteSessionStore } from "@/store/useSpInviteSessionStore";
import type { IStepsSchema } from "@/types/common.types";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { IconAlertHexagonFilled } from "@tabler/icons-react";
import { Badge } from "@onengine/component-library";

export const Route = createFileRoute("/sp-invite/review/_reviewLayout/$providerType/$stepId/")({
  component: ReviewStep,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

type LicenseOrCertificate = {
  name: string;
  tradeOrSubtrade: string;
  isMissing: boolean;
};

type LicenseOrCertificateItem = {
  name: string;
  extraAction?: ReactNode;
};

type CompensationLineItem = {
  lineItem?: string;
  percentage?: string;
};

type CompensationData = {
  yearlySalary?: string;
  salaryCadence?: string;
  doTheyTakeCommission?: boolean;
  commissionDetails?: {
    commissionBasis?: string;
    commissionLineItems?: CompensationLineItem[];
  };
};

const keysToSlugsMap = {
  ownerInformation: "personal-info",
  applicantInformation: "application-info",
  businessInformation: "business-info",
  trades: "trades",
  rates: "rates",
  payments: "payment-info",
  coverage: "coverage",
  compensation: "compensation",
  licensesAndCertifications: "licenses-certificates",
  documents: "documents",
};

type DocumentEntry = {
  uploaded?: boolean;
  fileName?: string | null;
};

function ReviewStep() {
  const { stepId, providerType } = Route.useParams();
  const navigate = useNavigate();

  const serviceProviderId = useSpInviteSessionStore((s) => s.splashData?.serviceProviderId ?? null);

  const handleGoToTab = (key: string) => {
    const slug = keysToSlugsMap[key as keyof typeof keysToSlugsMap];
    if (!slug) return;
    navigate({ to: `/sp-invite/review/${providerType}/${slug}` });
  };

  const { data: serviceProviderForm, isLoading, error } = useSpInviteForm(providerType);

  const {
    data: serviceProviderJsonForm,
    isLoading: isStepLoading,
    error: stepError,
  } = useSpInviteJsonForm(stepId as StepFormStepId, serviceProviderId ?? (null as any));

  useEffect(() => {
    if (!serviceProviderId && serviceProviderForm?.steps && stepId !== "personal-info") {
      const firstStep = serviceProviderForm.steps.find((step) => step.stepNumber === 1);
      if (firstStep) {
        navigate({
          to: "/sp-invite/review/$providerType/$stepId",
          params: { providerType, stepId: firstStep.stepKey },
          replace: true,
        });
      }
    }
  }, [serviceProviderId, stepId, providerType, navigate, serviceProviderForm]);

  if (isStepLoading) return <Text>Loading...</Text>;
  if (stepError) return <Text>Error loading data</Text>;

  if (stepId !== "personal-info" && !serviceProviderJsonForm) {
    return <Text>No Data found</Text>;
  }

  if (isLoading) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: "50vh" }}>
        <Loader size="small" />
      </Flex>
    );
  }
  if (error) return <Text>Error loading data</Text>;
  if (!serviceProviderForm || !serviceProviderForm.steps) return <Text>No Data found</Text>;

  const stepIndex = serviceProviderForm.steps.findIndex((step) => step.stepKey === stepId);

  const stepConfig =
    stepId === "personal-info" && !serviceProviderJsonForm
      ? serviceProviderForm.steps[stepIndex]
      : serviceProviderJsonForm;

  if (!stepConfig) {
    return <Text>Invalid step: {stepId}</Text>;
  }

  const renderStep = (
    _providerType: string,
    stepConfig: IStepsSchema | IAlignmentApplicantInfoFormProps,
  ) => {
    if (providerType === "w2" && stepId === "coverage") {
      return <W2Coverage sourceId={serviceProviderId!} />;
    } else {
      return <JsonFormBuilder jsonFromSchema={stepConfig} />;
    }
  };

  const extraAction = (
    <Badge
      color="rose"
      variant="outline"
      size="small"
      iconLeft={<IconAlertHexagonFilled size={14} />}
    >
      Not Provided
    </Badge>
  );

  const transformLicensesOrCertificates = (
    items?: LicenseOrCertificate[],
    keyPrefix?: string,
  ): Record<string, LicenseOrCertificateItem | string> | undefined => {
    if (!items) return undefined;

    if (items.length === 0) return { [keyPrefix ?? ""]: "-" };

    return items.reduce<Record<string, LicenseOrCertificateItem>>((acc, item, index) => {
      acc[`${keyPrefix} ${index + 1}`] = {
        name: item.tradeOrSubtrade,
        extraAction: item.isMissing ? extraAction : undefined,
      };
      return acc;
    }, {});
  };

  const transformCompensation = (
    compensation?: CompensationData,
  ): Record<string, string | Record<string, string>> | undefined => {
    if (!compensation || Object.keys(compensation).length === 0) return undefined;

    const transformed: Record<string, string | Record<string, string>> = {};

    if (compensation.yearlySalary) {
      transformed.yearlySalary = compensation.yearlySalary;
    }

    if (compensation.salaryCadence) {
      transformed.salaryCadence = compensation.salaryCadence;
    }

    if (typeof compensation.doTheyTakeCommission === "boolean") {
      transformed.doTheyTakeCommission = compensation.doTheyTakeCommission ? "Yes" : "No";
    }

    const commissionDetails = compensation.commissionDetails;

    if (commissionDetails) {
      if (commissionDetails.commissionBasis) {
        transformed.commissionBasis = commissionDetails.commissionBasis;
      }

      if (commissionDetails.commissionLineItems?.length) {
        transformed.commissionLineItems = commissionDetails.commissionLineItems
          .map(
            (item, index) =>
              `${item.lineItem || `Line Item ${index + 1}`} (${item.percentage || "-"})`,
          )
          .join(", ");
      }
    }

    return Object.keys(transformed).length ? transformed : undefined;
  };

  const transformDocuments = (
    documents?: Record<string, DocumentEntry>,
  ): Record<string, LicenseOrCertificateItem> | undefined => {
    if (!documents || Object.keys(documents).length === 0) return undefined;

    return Object.entries(documents).reduce<Record<string, LicenseOrCertificateItem>>(
      (acc, [key, value]) => {
        acc[key] = {
          name: value?.fileName ?? "",
          extraAction: value?.uploaded ? undefined : extraAction,
        };
        return acc;
      },
      {},
    );
  };

  const buildSummaryData = (data: any) => {
    const summaryData: Record<string, any> = {};

    if (data.applicantInformation && Object.keys(data.applicantInformation).length) {
      summaryData.applicantInformation = data.applicantInformation;
    }

    if (data.trades && Object.keys(data.trades).length) {
      summaryData.trades = data.trades;
    }

    const licenses = transformLicensesOrCertificates(
      data.licensesAndCertifications?.licenses,
      "licenses",
    );

    const certificates = transformLicensesOrCertificates(
      data.licensesAndCertifications?.certificates,
      "certificates",
    );

    if (licenses || certificates) {
      summaryData.licensesAndCertifications = {
        ...(licenses && { licenses }),
        ...(certificates && { certificates }),
      };
    }

    const transformedCompensation = transformCompensation(data.compensation);
    if (transformedCompensation) {
      summaryData.compensation = transformedCompensation;
    }

    if (data.coverage && Object.keys(data.coverage).length) {
      const { cities, ...restCoverage } = data.coverage;
      summaryData.coverage = {
        ...restCoverage,
        ...(cities !== undefined && { cities: Array.isArray(cities) ? cities.length : cities }),
      };
    }

    const documents = transformDocuments(data.documents);
    if (documents) {
      summaryData.documents = documents;
    }

    return summaryData;
  };

  return (
    <>
      {stepId === "summary" ? (
        <SummaryComponent
          summaryData={
            providerType === "w2"
              ? buildSummaryData(serviceProviderJsonForm)
              : serviceProviderJsonForm
          }
          handleGoToTab={handleGoToTab}
          showNotesCard={false}
        />
      ) : (
        renderStep(providerType, stepConfig)
      )}
    </>
  );
}
